/** @format */

import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	Box,
	Divider,
	Flex,
	Heading,
	Link,
	SimpleGrid,
	Text,
	VStack,
} from '@chakra-ui/react';
import IconTickLime from '../../../icons/IconTickLime';
import {textStyles} from '../../../styles/js/textStyles';

export const Pricing = ({
	joiningFee,
	annualFee,
}: {
	joiningFee: number;
	annualFee: number;
}) => (
	<Box py={4} px={6} minH='13.5rem'>
		<Box>
			<Text sx={textStyles.display4LeftDark}>£{joiningFee}</Text>
			<Text sx={textStyles.paragraph2LeftItalicDark}>
				One-off Joining Fee (+VAT)
			</Text>
		</Box>
		<Box>
			<Text sx={textStyles.display4LeftDark}>£{annualFee}</Text>
			<Text sx={textStyles.paragraph2LeftItalicDark}>
				Full annual subscription rate (+VAT)
			</Text>
		</Box>
	</Box>
);
const TickBullet = ({text}: {text: string}) => {
	const embraceScotlandUrl = 'https://www.embracescotland.co.uk';
	const isEmbraceScotland = text.includes('EmbraceScotland');

	const renderText = () => {
	  if (isEmbraceScotland) {
			const parts = text.split('EmbraceScotland');
			return (
		  <>
					{parts[0]}
					<Link isExternal href={embraceScotlandUrl} target='_blank' rel='noopener noreferrer' variant='nativeDark'>
			  			EmbraceScotland
					</Link>
					{parts[1]}
		  </>
			);
	  }

	  return text;
	};

	return (
	  <Flex>
			<Box minW={50} mr={2} mt={-1}>
		  <IconTickLime />
			</Box>
			<Text sx={textStyles.paragraph2LeftItalicDark}>{renderText()}</Text>
	  </Flex>
	);
};

export type TopCardProps = {
	text: {
		overline?: string;
		heading: string;
		body: string;
	};
	backgroundColor: string;
};

export const TopCard = ({text, backgroundColor}: TopCardProps) => {
	const {heading, body} = text;
	return (
		<Flex
			bgColor={backgroundColor}
			borderRadius='8px 8px 0 0'
			p={6}
			pb={6}
			minH={{base: 'auto', md: '280px'}}
			flexDirection='column'>
			<Text sx={textStyles.paragraph2LeftDark}>
				{text?.overline ? text.overline : 'Membership for...'}
			</Text>
			<Heading sx={textStyles.display5LeftDarkSemi} pb={4}>
				{heading}
			</Heading>
			<Text sx={textStyles.paragraph2LeftDark}>{body}</Text>
		</Flex>
	);
};

export type TickBulletsProps = {
	bullets: string[];
};

export const TickBullets = ({bullets}: TickBulletsProps) => (
	<VStack justifyContent='space-evenly' minH='19rem'>
		{bullets.map((text, index) => (
			<TickBullet key={text.concat(index.toString())} text={text} />
		))}
	</VStack>
);
const AccordionPricing = ({
	pricing,
}: {
	pricing: {
		annualFee: number;
		propertyFee: number;
		webpageFee: number;
	};
}) => {
	const {annualFee, propertyFee, webpageFee} = pricing;
	const style = {
		...textStyles.paragraph2LeftItalicDark,
		my: 6,
	};
	const headingStyle = {
		...textStyles.paragraph1LeftDark,
		my: 6,
	};
	return (
		<Box>
			<Text sx={headingStyle}>Pricing as above plus:</Text>
			<Text sx={style}>
				Additional annual subscription rate of £{annualFee / 100} + VAT
			</Text>
			<Text sx={style}>
				£{propertyFee / 100} + VAT per additional property listed
			</Text>
			<Text sx={style}>
				£{webpageFee / 100} + VAT per additional webpage added
			</Text>
		</Box>
	);
};

export type MarketingPackageAccordionProps = {
	packageInfo: string;
	bullets: string[];
	pricing: {
		annualFee: number;
		propertyFee: number;
		webpageFee: number;
	};
};

export const MarketingPackageAccordion = ({
	packageInfo,
	bullets,
	pricing,
}: MarketingPackageAccordionProps) => (
	<Accordion allowToggle>
		<AccordionItem>
			<h3>
				<AccordionButton p={6}>
					<SimpleGrid columns={{base: 1}} spacing={1} textAlign='left'>
						<Text sx={textStyles.display7LeftDarkSemi}>Marketing Package</Text>
						<Text sx={textStyles.display7LeftGreenSemi}>Add-on</Text>
					</SimpleGrid>
					<AccordionIcon ml='auto' />
				</AccordionButton>
			</h3>
			<AccordionPanel px={6}>
				<Text sx={textStyles.paragraph2LeftItalicDark} pb={6}>
					{packageInfo}
				</Text>
				<Box mb={6}>
					<TickBullets bullets={bullets} />
				</Box>
				<Divider variant='primary' />
				<AccordionPricing pricing={pricing} />
			</AccordionPanel>
		</AccordionItem>
	</Accordion>
);

export type TextSectionProps = {
	heading: string;
	body: string;
};

export type PricingProps = {
	joiningFee: number;
	annualFee: number;
};

export const TextSection = ({heading, body}: TextSectionProps) => (
	<>
		<Text sx={textStyles.display7LeftDarkSemi} mb={2} mt={2}>
			{heading}
		</Text>
		<Text sx={textStyles.paragraph2LeftItalicDark}>{body}</Text>
	</>
);

export default Pricing;
